// quote-cart.js - Complete Quote Cart System with Debouncing

if (!customElements.get('quote-cart')) {
  customElements.define('quote-cart', class QuoteCart extends HTMLElement {
    constructor() {
      super();
      this.items = this.getStoredItems();
      this.init();
    }

    init() {
      // Listen for quote events
      document.addEventListener('quote:added', this.handleQuoteAdd.bind(this));
      document.addEventListener('quote:removed', this.handleQuoteRemove.bind(this));
      document.addEventListener('quote:updated', this.handleQuoteUpdate.bind(this));
      document.addEventListener('quote:submitted', this.handleQuoteSubmit.bind(this));
      
      this.updateDisplay();
    }

    getStoredItems() {
      try {
        return JSON.parse(sessionStorage.getItem('quoteCart') || '[]');
      } catch (e) {
        return [];
      }
    }

    saveItems() {
      try {
        sessionStorage.setItem('quoteCart', JSON.stringify(this.items));
      } catch (e) {
        console.warn('Could not save quote cart to session storage');
      }
    }

    handleQuoteAdd(event) {
      const productData = event.detail;
      const existingIndex = this.items.findIndex(item => 
        item.variant_id === productData.variant_id
      );

      if (existingIndex >= 0) {
        // Item already exists - don't add again, just show notification
        this.showNotification(`${productData.title} is already in your quote`);
        return;
      } else {
        // Add new item with quantity 1
        this.items.push({
          ...productData,
          quantity: 1,
          added_at: new Date().toISOString()
        });
      }

      this.saveItems();
      this.updateDisplay();
      this.showNotification(`${productData.title} added to quote`);
    }

    handleQuoteRemove(event) {
      const variantId = event.detail.variant_id;
      this.items = this.items.filter(item => item.variant_id !== variantId);
      this.saveItems();
      this.updateDisplay();
    }

    handleQuoteUpdate(event) {
      const { variant_id, quantity } = event.detail;
      const itemIndex = this.items.findIndex(item => item.variant_id === variant_id);
      
      if (itemIndex >= 0) {
        if (quantity <= 0) {
          this.items.splice(itemIndex, 1);
        } else {
          this.items[itemIndex].quantity = quantity;
        }
        this.saveItems();
        this.updateDisplay();
      }
    }

    handleQuoteSubmit(event) {
      // Clear quote cart after successful submission
      this.items = [];
      this.saveItems();
      this.updateDisplay();
    }

    updateDisplay() {
      // Update quote button badge
      const quoteBadges = document.querySelectorAll('.quote-count-bubble');
      const quoteButtons = document.querySelectorAll('[data-quote-count]');
      
      quoteBadges.forEach(badge => {
        if (this.items.length > 0) {
          badge.textContent = this.items.length;
          badge.style.display = 'block';
        } else {
          badge.style.display = 'none';
        }
      });

      quoteButtons.forEach(button => {
        button.setAttribute('data-quote-count', this.items.length);
      });

      // Update quote drawer/modal content
      this.updateQuoteDrawerContent();
    }

    updateQuoteDrawerContent() {
      const quoteItemsDisplay = document.getElementById('quote-items-display');
      const quoteItemsList = document.getElementById('quote-items-list');
      
      if (!quoteItemsDisplay || !quoteItemsList) return;

      if (this.items.length === 0) {
        quoteItemsDisplay.style.display = 'none';
        return;
      }

      quoteItemsDisplay.style.display = 'block';
      quoteItemsList.innerHTML = this.items.map(item => `
        <div class="quote-item" style="display: flex; align-items: center; gap: 12px; padding: 8px; border: 1px solid #e0e0e0; border-radius: 4px; margin-bottom: 8px;">
          ${item.image ? `
            <img src="${item.image}" alt="${item.title}" 
                 style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px;">
          ` : ''}
          <div style="flex-grow: 1; min-width: 0;">
            <div style="font-size: 14px; font-weight: 500; margin-bottom: 2px;">${item.title}</div>
            ${item.variant_title && item.variant_title !== 'Default Title' ? 
              `<div style="font-size: 12px; color: #666;">${item.variant_title}</div>` : ''}
            <div style="font-size: 12px; color: #888; display: flex; align-items: center; gap: 8px;">
              <span>Qty:</span>
              <input type="number" 
                     value="${item.quantity}" 
                     min="1" 
                     max="999"
                     style="width: 60px; padding: 2px 4px; border: 1px solid #ccc; border-radius: 2px; text-align: center;"
                     onchange="window.quoteCart.updateItemQuantity('${item.variant_id}', this.value)"
                     onclick="this.select()">
              <span>• ${item.price}</span>
            </div>
          </div>
          <button onclick="window.quoteCart.removeItem('${item.variant_id}')" 
                  style="background: none; border: none; color: #999; cursor: pointer; padding: 4px;">
            ✕
          </button>
        </div>
      `).join('');
    }

    updateItemQuantity(variantId, newQuantity) {
      const quantity = parseInt(newQuantity);
      if (quantity < 1) return; // Don't allow quantities less than 1
      
      document.dispatchEvent(new CustomEvent('quote:updated', {
        detail: { 
          variant_id: parseInt(variantId), 
          quantity: quantity 
        }
      }));
    }

    removeItem(variantId) {
      document.dispatchEvent(new CustomEvent('quote:removed', {
        detail: { variant_id: parseInt(variantId) }
      }));
    }

    // populateHeaderForm method removed - no more auto-population

    showNotification(message) {
      // Create a simple notification
      const notification = document.createElement('div');
      notification.className = 'quote-notification';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #4CAF50;
        color: white;
        padding: 12px 20px;
        border-radius: 4px;
        z-index: 10000;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        font-size: 14px;
        transition: all 0.3s ease;
      `;
      notification.textContent = message;
      
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.style.opacity = '0';
        notification.style.transform = 'translateX(100%)';
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }

    getItemCount() {
      return this.items.length;
    }

    getItems() {
      return [...this.items];
    }

    clearCart() {
      this.items = [];
      this.saveItems();
      this.updateDisplay();
    }
  });
}

// Initialize quote cart when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Create and attach quote cart to window for global access
  const quoteCartElement = document.createElement('quote-cart');
  document.body.appendChild(quoteCartElement);
  window.quoteCart = quoteCartElement;

  // Initialize quote buttons
  initializeQuoteButtons();
});

// Add debouncing variables
let isProcessing = false;
let lastClickTime = 0;
const DEBOUNCE_DELAY = 500; // 500ms delay

function initializeQuoteButtons() {
  // Handle quote button clicks on product pages
  document.addEventListener('click', function(e) {
    if (e.target.matches('[data-action="add-to-quote"]') || 
        e.target.closest('[data-action="add-to-quote"]')) {
      
      // Prevent rapid clicking and double-click events
      const currentTime = Date.now();
      if (isProcessing || (currentTime - lastClickTime) < DEBOUNCE_DELAY) {
        console.log('Quote button click ignored - too soon after last click');
        return;
      }
      
      isProcessing = true;
      lastClickTime = currentTime;
      
      const button = e.target.matches('[data-action="add-to-quote"]') ? 
                    e.target : e.target.closest('[data-action="add-to-quote"]');
      
      // Temporarily disable the button to prevent multiple clicks
      const originalDisabled = button.disabled;
      button.disabled = true;
      
      const productData = {
        id: parseInt(button.dataset.productId),
        variant_id: parseInt(button.dataset.variantId),
        title: button.dataset.productTitle,
        variant_title: button.dataset.variantTitle || 'Default Title',
        price: button.dataset.price,
        image: button.dataset.image,
        url: button.dataset.url
      };

      console.log('Adding to quote:', productData); // Debug log

      document.dispatchEvent(new CustomEvent('quote:added', {
        detail: productData
      }));
      
      // Re-enable button and reset processing flag after delay
      setTimeout(() => {
        button.disabled = originalDisabled;
        isProcessing = false;
      }, DEBOUNCE_DELAY);
    }
  });
}